/**
 * Campaigns Listing Page
 * 
 * Main page for managing promotional campaigns with:
 * - Table listing all campaigns
 * - Filters by status and campaign type
 * - Search functionality
 * - Pagination
 * - Actions (create, edit, delete, activate/deactivate)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Card, Row, Col, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Common components
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PageHeader from '../../../Common/PageHeader';
import { SuccessModal } from '../../../Common/SuccessModal';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import FilterSidebar, { FilterValues, FilterConfig } from '../../../Common/FilterSidebar';

// Campaign specific components
import CampaignTable from './components/CampaignTable';
import Pagination from '../../../pages/ShopServices/Orders/components/Pagination';
import './Campaigns.css';

// Hooks
import { useDebounce } from '../../../hooks/useDebounce';

// API and types
import { getCampaigns, deleteCampaign, updateCampaignStatus } from '../../../api/promos';
import { Campaign, CampaignListParams, CampaignStatus, CampaignType } from '../../../api/promos/types';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'promo.admin',
    'promo.campaigns.manage',
  ]);
  
  // State management
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  // Removed duplicate currentPage state - using filters.pageNum as single source of truth
  
  // Filters state
  const [filters, setFilters] = useState<CampaignListParams>({
    status: undefined,
    campaignType: undefined,
    search: '',
    pageNum: 1,
    pageSize: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    startAt: undefined,
    endAt: undefined,
  });
  
  // Derived value for currentPage (using filters.pageNum as source of truth)
  const currentPage = filters.pageNum || 1;
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter sidebar state
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  
  // Search input state (for immediate UI updates)
  const [searchInputValue, setSearchInputValue] = useState<string>(filters.search || '');
  
  // Debounce search input to prevent excessive API calls (300ms delay)
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Sync filterValues with filters when filters change (but only if sidebar is closed to avoid conflicts)
  useEffect(() => {
    if (!showFilterSidebar) {
      setFilterValues({
        status: filters.status || undefined,
        campaignType: filters.campaignType || undefined,
        startDate: filters.startAt || undefined,
        endDate: filters.endAt || undefined,
      });
    }
  }, [filters, showFilterSidebar]);
  
  // Sync searchInputValue with filters.search when filters change externally (e.g., from filter sidebar)
  useEffect(() => {
    if (filters.search !== searchInputValue && filters.search !== debouncedSearchTerm) {
      setSearchInputValue(filters.search || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);
  
  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearchTerm !== filters.search) {
      handleFilterChange({ search: debouncedSearchTerm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);
  
  // Fetch campaigns data
  const fetchCampaigns = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getCampaigns(filters);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setCampaigns(response.campaigns || []);
      setTotalCount(response.totalCount || 0);
      // Removed setCurrentPage - currentPage is now derived from filters.pageNum
    } catch (err: any) {
      // Don't set error if request was aborted (user changed filters or component unmounted)
      if (abortController.signal.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.message || 'Failed to load campaigns. Please try again.');
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters]);
  
  // Load campaigns on mount and filter changes
  useEffect(() => {
    fetchCampaigns();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCampaigns]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<CampaignListParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      pageNum: 1, // Reset to first page when filters change
    }));
  }, []);
  
  // Handle search input change (immediate UI update, debounced API call)
  const handleSearchInputChange = useCallback((searchTerm: string) => {
    setSearchInputValue(searchTerm);
  }, []);
  
  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInputValue('');
    handleFilterChange({ search: '' });
  }, [handleFilterChange]);
  
  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({
      ...prev,
      pageNum: page,
    }));
  }, []);
  
  // Handle page size change
  const handlePageSizeChange = useCallback((size: number) => {
    setFilters(prev => ({
      ...prev,
      pageSize: size,
      pageNum: 1, // Reset to first page
    }));
  }, []);
  
  // Handle create new campaign
  const handleCreateCampaign = () => {
    if (!hasManagePermissions) return;
    navigate('/promos/campaigns/create');
  };
  
  // Handle edit campaign
  const handleEditCampaign = useCallback((campaign: Campaign) => {
    navigate(`/promos/campaigns/${campaign.id}/edit`);
  }, [navigate]);
  
  // Handle view campaign
  const handleViewCampaign = useCallback((campaign: Campaign) => {
    navigate(`/promos/campaigns/${campaign.id}/detail`);
  }, [navigate]);
  
  // Handle archive campaign (using update-status endpoint)
  const handleArchiveCampaign = useCallback(async (campaign: Campaign) => {
    try {
      await updateCampaignStatus(campaign.id, CampaignStatus.ARCHIVED);
      
      setSuccessTitle('Campaign Archived');
      setSuccessMessage(`Campaign "${campaign.name}" has been archived successfully.`);
      setShowSuccessModal(true);
      
      // Refresh campaigns list
      fetchCampaigns();
    } catch (err: any) {
      setError(err.message || 'Failed to archive campaign. Please try again.');
    }
  }, [fetchCampaigns]);
  
  // Handle delete campaign
  const handleDeleteCampaign = useCallback((campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDeleteModal(true);
  }, []);
  
  // Confirm delete campaign
  const confirmDeleteCampaign = async () => {
    if (!selectedCampaign) return;
    
    try {
      setIsDeleting(true);
      await deleteCampaign(selectedCampaign.id);
      
      setShowDeleteModal(false);
      setSelectedCampaign(null);
      
      // Show success message
      setSuccessTitle('Campaign Deleted');
      setSuccessMessage(`Campaign "${selectedCampaign.name}" has been successfully deleted.`);
      setShowSuccessModal(true);
      
      // Refresh campaigns list
      fetchCampaigns();
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle activate/deactivate campaign (Pause/Activate)
  const handleToggleStatus = useCallback(async (campaign: Campaign) => {
    try {
      // Determine new status
      const newStatus = campaign.status === CampaignStatus.ACTIVE 
        ? CampaignStatus.PAUSED 
        : CampaignStatus.ACTIVE;
      
      // Use update-status endpoint for status changes
      await updateCampaignStatus(campaign.id, newStatus);
      
      const action = newStatus === CampaignStatus.ACTIVE ? 'Activated' : 'Paused';
      setSuccessTitle(`Campaign ${action}`);
      setSuccessMessage(`Campaign "${campaign.name}" has been ${action.toLowerCase()}.`);
      
      setShowSuccessModal(true);
      
      // Refresh campaigns list
      fetchCampaigns();
    } catch (err: any) {
      setError(err.message || 'Failed to update campaign status. Please try again.');
    }
  }, [fetchCampaigns]);
  
  // Handle duplicate campaign
  const handleDuplicateCampaign = useCallback((campaign: Campaign) => {
    navigate('/promos/campaigns/create', {
      state: { duplicateFrom: campaign }
    });
  }, [navigate]);
  
  // Filter sidebar handlers
  const handleOpenFilterSidebar = () => {
    setShowFilterSidebar(true);
  };
  
  const handleCloseFilterSidebar = () => {
    setShowFilterSidebar(false);
  };
  
  const handleApplyFilters = (filters: FilterValues) => {
    // Validate date range before applying
    if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
      // Don't apply filters if date range is invalid
      // The FilterSidebar component will show validation error
      return;
    }
    
    setFilterValues(filters);
    handleFilterChange({
      status: filters.status as CampaignStatus | undefined,
      campaignType: filters.campaignType as CampaignType | undefined,
      startAt: filters.startDate,
      endAt: filters.endDate,
    });
  };
  
  const handleResetFilters = () => {
    setFilterValues({});
    handleFilterChange({
      status: undefined,
      campaignType: undefined,
      startAt: undefined,
      endAt: undefined,
    });
  };
  
  const handleFilterChangeLocal = (key: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };
  
  // Filter configuration
  const filterConfig: FilterConfig = {
    domain: {
      label: 'Domain',
      options: [
        { value: 'all', label: 'All' },
        { value: 'sports', label: 'Sports' },
        { value: 'events', label: 'Events' },
      ],
    },
    status: {
      label: 'Status',
      options: [
        { value: 'all', label: 'All' },
        { value: CampaignStatus.ACTIVE, label: 'Active' },
        { value: CampaignStatus.INACTIVE, label: 'Inactive' },
        { value: CampaignStatus.EXPIRED, label: 'Expired' },
        { value: CampaignStatus.SCHEDULED, label: 'Scheduled' },
        { value: CampaignStatus.DRAFT, label: 'Draft' },
      ],
    },
    campaignType: {
      label: 'Campaign Type',
      options: [
        { value: 'all', label: 'All' },
        { value: CampaignType.FESTIVAL, label: 'Festival' },
        { value: CampaignType.SEASONAL, label: 'Seasonal' },
        { value: CampaignType.PERFORMANCE, label: 'Performance' },
        { value: CampaignType.PARTNER, label: 'Partner' },
        { value: CampaignType.INTERNAL, label: 'Internal' },
      ],
    },
    dateRange: {
      label: 'Date Range',
      startDateLabel: 'Start Date*',
      endDateLabel: 'End Date*',
    },
  };
  
  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'Campaigns', active: true }
            ]} 
          />
        </div>
        
        {/* Page Header */}
        <PageHeader
          breadcrumb={null}
          title="Campaigns"
          addNewLabel={hasManagePermissions ? "+ Create Campaign" : undefined}
          onAddNew={hasManagePermissions ? handleCreateCampaign : undefined}
        />
        
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Main Content */}
        <Row>
          <Col lg={12}>
            <Card>
              <Card.Body>
                {/* Search Bar and Filter Button */}
                <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
                  {/* Search Bar - Left Side */}
                  <div style={{ position: 'relative', minWidth: '250px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by promo or user"
                      value={searchInputValue}
                      onChange={(e) => handleSearchInputChange(e.target.value)}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                        e.target.style.boxShadow = 'none';
                        e.target.style.outline = 'none';
                      }}
                      style={{
                        paddingRight: searchInputValue ? '35px' : '12px',
                        paddingLeft: '40px',
                        height: '36px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        fontSize: '14px',
                        outline: 'none',
                        boxShadow: 'none',
                      }}
                    />
                    {/* Search Icon */}
                    <i
                      className="ph-magnifying-glass"
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6B7280',
                        fontSize: '18px',
                        pointerEvents: 'none',
                      }}
                    />
                    {/* Clear Icon */}
                    {searchInputValue && (
                      <button
                        type="button"
                        onClick={handleSearchClear}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px',
                          color: '#6B7280',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                        }}
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <i className="ph-x"></i>
                      </button>
                    )}
                  </div>
                  
                  {/* Filter Button - Right Side */}
                  <button
                    type="button"
                    onClick={handleOpenFilterSidebar}
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      background: '#FFFFFF',
                      color: '#374151',
                      fontSize: '14px',
                      fontWeight: 500,
                      height: '36px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFFFFF';
                    }}
                    title="Filter"
                    aria-label="Filter"
                  >
                    <img
                      src="https://cdn-dev.sportsforlife.in/admin/static-assets/icons/general/filter-Icon.svg"
                      alt="Filter"
                      style={{ width: '20px', height: '20px' }}
                      onError={(e) => {
                        // Fallback to dev-api if CDN fails
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://dev-api.sportsforlife.in/admin/static-assets/icons/general/filter-Icon.svg';
                        target.onerror = () => {
                          // Final fallback to icon
                          target.style.display = 'none';
                          const icon = document.createElement('i');
                          icon.className = 'ph-funnel';
                          icon.style.fontSize = '18px';
                          icon.style.color = '#374151';
                          target.parentElement?.appendChild(icon);
                        };
                      }}
                    />
                    Filter
                  </button>
                </div>
                
                {/* Table */}
                {loading ? (
                  <ShimmerLoader type="table" rows={5} />
                ) : campaigns.length > 0 ? (
                  <CampaignTable
                    campaigns={campaigns}
                    totalCount={totalCount}
                    currentPage={currentPage}
                    pageSize={filters.pageSize || 10}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onView={handleViewCampaign}
                    onEdit={hasManagePermissions ? handleEditCampaign : undefined}
                    onDelete={hasManagePermissions ? handleDeleteCampaign : undefined}
                    onArchive={hasManagePermissions ? handleArchiveCampaign : undefined}
                    onToggleStatus={hasManagePermissions ? handleToggleStatus : undefined}
                    onDuplicate={hasManagePermissions ? handleDuplicateCampaign : undefined}
                  />
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="ph-folder-notch display-4 text-muted"></i>
                    </div>
                    <h5 className="text-muted">No campaigns found</h5>
                    <p className="text-muted mb-3">
                      {filters.search || filters.status || filters.campaignType
                        ? 'Try adjusting your search criteria or filters.'
                        : 'Get started by creating your first promotional campaign.'
                      }
                    </p>
                    {hasManagePermissions && (
                      <Button variant="primary" onClick={handleCreateCampaign}>
                        + Create Campaign
                      </Button>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
            
            {/* Pagination - Outside Card to match Products page structure */}
            {!loading && Math.ceil(totalCount / (filters.pageSize || 10)) > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / (filters.pageSize || 10))}
                totalCount={totalCount}
                pageSize={filters.pageSize || 10}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </Col>
        </Row>
        
        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          title={successTitle}
          message={successMessage}
        />
        
        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          deleteModalFunction={confirmDeleteCampaign}
          title="Delete Campaign"
          message={`Are you sure you want to delete the campaign "${selectedCampaign?.name}"? This action cannot be undone.`}
          isLoading={isDeleting}
        />
        
        {/* Filter Sidebar */}
        <FilterSidebar
          show={showFilterSidebar}
          onClose={handleCloseFilterSidebar}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          filters={filterValues}
          filterConfig={filterConfig}
          onFilterChange={handleFilterChangeLocal}
        />
      </Container>
    </div>
  );
};

export default CampaignsPage;